import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

// NOTE: The previous implementation kept the token in memory only to avoid
// XSS exposure. This implementation persists to localStorage per product
// requirements so tokens survive page reload without flicker. The correct
// long-term solution remains httpOnly cookies issued by the server.

/**
 * Tracks an active platform-admin impersonation session. The token itself
 * lives in `token`; this metadata is for UX (banner + sidebar pill) and to
 * guard the SPA's login form so an operator can't accidentally start a fresh
 * session on top of an impersonated one.
 *
 * `active === false` is the normal (non-impersonated) state; the other fields
 * are null. When `active === true`, the fields below are populated from JWT
 * claims minted by the provisioner's `/admin/impersonation/start` endpoint.
 */
export interface ImpersonationState {
  active: boolean;
  /** Email of the platform admin who started the session. Displayed in the sidebar. */
  impersonatedBy: string | null;
  /** Server-side correlation id; sent back on /auth/impersonate/end. */
  impersonationId: string | null;
  /** ISO-8601 expiry from the JWT `exp` claim. UI uses this to fade out near expiry. */
  expiresAt: string | null;
}

const EMPTY_IMPERSONATION: ImpersonationState = {
  active: false,
  impersonatedBy: null,
  impersonationId: null,
  expiresAt: null,
};

interface AuthState {
  userId: string | null;
  email: string | null;
  token: string | null;
  refreshToken: string | null;
  tenantId: string | null;
  roles: string[];
  permissions: string[];
  permissionsVersion: number;
  permissionScopes: Record<string, string>;
  capabilityFlags: Record<string, boolean>;
  fieldMasks: string[];
  /** FIX-myday — linked technician id (Technician.authUserId) or null. Gates
   *  the "My Day" surface by the real link, not the role name. */
  technicianId: string | null;
  impersonation: ImpersonationState;
  login: (params: LoginParams) => void;
  logout: () => void;
  setToken: (token: string, refreshToken?: string) => void;
  refreshPermissions: (params: RefreshPermissionsParams) => void;
  /**
   * Atomically replaces the session with an impersonation session. Wipes any
   * previous credentials first so we don't carry the operator's normal
   * session through into the impersonated tab.
   */
  startImpersonation: (params: StartImpersonationParams) => void;
}

export interface RefreshPermissionsParams {
  permissions: string[];
  permissionsVersion: number;
  permissionScopes?: Record<string, string>;
  capabilityFlags?: Record<string, boolean>;
  fieldMasks?: string[];
  /** Carried by /auth/permissions so a silent refresh hydrates the link on a
   *  live session without a forced re-login. */
  technicianId?: string | null;
}

export interface LoginParams {
  userId: string;
  email?: string;
  token: string;
  refreshToken: string;
  tenantId: string;
  roles: string[];
  permissions: string[];
  permissionsVersion: number;
  permissionScopes?: Record<string, string>;
  capabilityFlags?: Record<string, boolean>;
  fieldMasks?: string[];
  technicianId?: string | null;
}

export interface StartImpersonationParams {
  userId: string;
  email?: string;
  token: string;
  tenantId: string;
  impersonatedBy: string;
  impersonationId: string;
  /** ISO-8601 timestamp of the JWT `exp` claim. */
  expiresAt: string;
  roles?: string[];
  permissions?: string[];
  permissionsVersion?: number;
}

/** localStorage key the persisted auth state is written under. Other tabs read
 *  it to adopt a renewed access token — see `lib/api/session.ts`. */
export const AUTH_STORE_KEY = 'auth-store';

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        userId: null,
        email: null,
        token: null,
        refreshToken: null,
        tenantId: null,
        roles: [],
        permissions: [],
        permissionsVersion: 0,
        permissionScopes: {},
        capabilityFlags: {},
        fieldMasks: [],
        technicianId: null,
        impersonation: EMPTY_IMPERSONATION,

        login: (params) =>
          set(
            {
              userId: params.userId,
              email: params.email ?? null,
              token: params.token,
              refreshToken: params.refreshToken,
              tenantId: params.tenantId,
              roles: params.roles,
              permissions: params.permissions,
              permissionsVersion: params.permissionsVersion,
              permissionScopes: params.permissionScopes ?? {},
              capabilityFlags: params.capabilityFlags ?? {},
              fieldMasks: params.fieldMasks ?? [],
              technicianId: params.technicianId ?? null,
              // A normal login always exits impersonation — we never want both
              // modes active at once.
              impersonation: EMPTY_IMPERSONATION,
            },
            false,
            'auth/login',
          ),

        startImpersonation: (params) =>
          set(
            {
              userId: params.userId,
              email: params.email ?? null,
              token: params.token,
              // Impersonation tokens are single-use and have no refresh flow.
              refreshToken: null,
              tenantId: params.tenantId,
              roles: params.roles ?? [],
              permissions: params.permissions ?? [],
              permissionsVersion: params.permissionsVersion ?? 0,
              permissionScopes: {},
              capabilityFlags: {},
              fieldMasks: [],
              // Impersonation tokens never carry a technician link.
              technicianId: null,
              impersonation: {
                active: true,
                impersonatedBy: params.impersonatedBy,
                impersonationId: params.impersonationId,
                expiresAt: params.expiresAt,
              },
            },
            false,
            'auth/startImpersonation',
          ),

        logout: () => {
          // Clear credentials synchronously first so any code that reads the
          // store immediately after logout() sees null state. Dynamic imports
          // for cleanup side-effects are fire-and-forget — they must not block
          // the state clear.
          set(
            {
              userId: null,
              email: null,
              token: null,
              refreshToken: null,
              tenantId: null,
              roles: [],
              permissions: [],
              permissionsVersion: 0,
              permissionScopes: {},
              capabilityFlags: {},
              fieldMasks: [],
              technicianId: null,
              impersonation: EMPTY_IMPERSONATION,
            },
            false,
            'auth/logout',
          );

          // Dynamic imports break the circular module dependency at load time.
          // All stores are fully initialised by the time logout() is called.
          import('./voip.store').then(({ useVoipStore }) => {
            useVoipStore.getState().hangup();
          });
          // Clear stale tenant context so feature-flag checks after logout
          // cannot use the previous tenant's data.
          import('./tenant.store').then(({ useTenantStore }) => {
            useTenantStore.getState().clearTenant();
          });

          // Clear the last-login-email hint. Without this, a return visit
          // to /login would pre-fill the previous user's email on a shared
          // machine, and the IconRail's defensive fallback would show that
          // email when impersonation enrichment is in flight. Mirrors the
          // write site in routes/(public)/login.tsx + invite.$token.tsx.
          // localStorage can throw in private mode — best-effort only.
          try {
            localStorage.removeItem('notifi:last-email');
          } catch {
            // ignore
          }
        },

        setToken: (token, refreshToken?) =>
          set(
            (state) => ({
              token,
              refreshToken: refreshToken ?? state.refreshToken,
            }),
            false,
            'auth/setToken',
          ),

        refreshPermissions: (params) =>
          set(
            (state) => ({
              permissions: params.permissions,
              permissionsVersion: params.permissionsVersion,
              permissionScopes: params.permissionScopes ?? {},
              capabilityFlags: params.capabilityFlags ?? {},
              fieldMasks: params.fieldMasks ?? [],
              // Honor an explicit value (including null = unlinked); keep the
              // current link when a caller omits it entirely.
              technicianId:
                params.technicianId !== undefined ? params.technicianId : state.technicianId,
            }),
            false,
            'auth/refreshPermissions',
          ),
      }),
      {
        name: AUTH_STORE_KEY,
        // No version bump needed: dropping the monolith fields from
        // `partialize` is backward-compatible — a previously-persisted blob
        // just carries extra keys nothing reads anymore.
        version: 6,
        // Only persist the credentials — actions are recreated on every load.
        // refreshToken is intentionally excluded: storing it in localStorage
        // would allow any XSS script to silently refresh access tokens and
        // achieve persistent account takeover. Keep it in memory only.
        partialize: (state) => ({
          userId: state.userId,
          email: state.email,
          token: state.token,
          tenantId: state.tenantId,
          roles: state.roles,
          permissions: state.permissions,
          permissionsVersion: state.permissionsVersion,
          permissionScopes: state.permissionScopes,
          capabilityFlags: state.capabilityFlags,
          fieldMasks: state.fieldMasks,
          technicianId: state.technicianId,
          // Persist impersonation so a page refresh (or accidental tab close /
          // reopen) doesn't drop the banner and leave the operator unaware they
          // are still in an impersonated session.
          impersonation: state.impersonation,
        }),
      },
    ),
    { name: 'AuthStore', enabled: import.meta.env.DEV },
  ),
);

// Typed selector hooks
export const useAuthToken = () => useAuthStore((s) => s.token);
export const useAuthUserId = () => useAuthStore((s) => s.userId);
export const useAuthEmail = () => useAuthStore((s) => s.email);
export const useAuthTenantId = () => useAuthStore((s) => s.tenantId);
export const useAuthRoles = () => useAuthStore((s) => s.roles);
export const useAuthTechnicianId = () => useAuthStore((s) => s.technicianId);
export const useAuthPermissions = () => useAuthStore((s) => s.permissions);
export const useAuthPermissionsVersion = () => useAuthStore((s) => s.permissionsVersion);
export const useAuthPermissionScopes = () => useAuthStore((s) => s.permissionScopes);
export const useAuthCapabilityFlags = () => useAuthStore((s) => s.capabilityFlags);
export const useAuthFieldMasks = () => useAuthStore((s) => s.fieldMasks);
export const useAuthActions = () =>
  useAuthStore(
    useShallow((s) => ({
      login: s.login,
      logout: s.logout,
      setToken: s.setToken,
      refreshPermissions: s.refreshPermissions,
      startImpersonation: s.startImpersonation,
    })),
  );

// ─── Impersonation selectors ────────────────────────────────────────────────

export const useImpersonation = () => useAuthStore((s) => s.impersonation);
export const useIsImpersonating = () => useAuthStore((s) => s.impersonation.active);
